import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../../utils/apiClient';
import Modal from '../common/Modal';
import { renderContent } from '../renderContent';

const BASE = process.env.REACT_APP_API_BASE_URL;

export default function AdminMedia() {
    const [mediaList, setMediaList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    
    // Modal states
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingMedia, setEditingMedia] = useState(null);
    const [busy, setBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [expandedMediaTags, setExpandedMediaTags] = useState({});
    const [previewMedia, setPreviewMedia] = useState(null);

    const toggleExpandTags = (mediaId) => {
        setExpandedMediaTags(prev => ({
            ...prev,
            [mediaId]: !prev[mediaId]
        }));
    };

    // Form states
    const [title, setTitle] = useState('');
    const [type, setType] = useState('image');
    const [description, setDescription] = useState('');
    const [keywords, setKeywords] = useState('');
    const [content, setContent] = useState('');
    const [file, setFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);

    const fileInputRef = useRef(null);

    const handleAiSuggest = async () => {
        setErrorMessage('');
        setSuccessMessage('');
        
        if (!file && !content.trim()) {
            setErrorMessage("Veuillez sélectionner un fichier ou spécifier une URL/description avant de demander l'analyse de l'IA.");
            return;
        }

        setAiLoading(true);
        try {
            const formData = new FormData();
            if (file) {
                formData.append('media_file', file);
            } else {
                formData.append('hint', content);
            }
            formData.append('filename', file ? file.name : '');

            const res = await apiClient(`${BASE}/admin_media.php?action=suggest`, {
                method: 'POST',
                body: formData
            });

            const json = await res.json();
            if (json.success && json.suggestions) {
                const s = json.suggestions;
                if (s.title) setTitle(s.title);
                if (s.description) setDescription(s.description);
                if (s.keywords) setKeywords(s.keywords);
                setSuccessMessage("L'IA a généré avec succès les métadonnées pour ce média !");
            } else {
                setErrorMessage(json.message || "L'IA n'a pas pu analyser le média.");
            }
        } catch (err) {
            setErrorMessage("Erreur lors de l'appel à l'IA.");
            console.error(err);
        } finally {
            setAiLoading(false);
        }
    };

    const loadMedia = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient(`${BASE}/admin_media.php?action=list`);
            const json = await res.json();
            if (json.success) {
                setMediaList(json.media || []);
            }
        } catch (error) {
            console.error("Error loading media:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadMedia();
    }, [loadMedia]);

    // Handle opening Form Modal for Create
    const handleOpenCreate = () => {
        setEditingMedia(null);
        setTitle('');
        setType('image');
        setDescription('');
        setKeywords('');
        setContent('');
        setFile(null);
        setFilePreview(null);
        setErrorMessage('');
        setSuccessMessage('');
        setIsFormOpen(true);
    };

    // Handle opening Form Modal for Edit
    const handleOpenEdit = (media) => {
        setEditingMedia(media);
        setTitle(media.title || '');
        setType(media.type || 'image');
        setDescription(media.description || '');
        setKeywords(media.keywords || '');
        setContent(media.content || '');
        setFile(null);
        setFilePreview(null);
        setErrorMessage('');
        setSuccessMessage('');
        setIsFormOpen(true);
    };

    // Handle drag and drop preview
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            
            // Auto detect type
            const mime = selectedFile.type;
            if (mime.startsWith('video/')) {
                setType('video');
            } else if (mime.startsWith('audio/')) {
                setType('audio');
            } else if (mime.startsWith('image/')) {
                setType('image');
            } else {
                setType('other');
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setFilePreview(reader.result);
            };
            reader.readAsDataURL(selectedFile);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            
            // Auto detect type
            const mime = droppedFile.type;
            if (mime.startsWith('video/')) {
                setType('video');
            } else if (mime.startsWith('audio/')) {
                setType('audio');
            } else if (mime.startsWith('image/')) {
                setType('image');
            } else {
                setType('other');
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setFilePreview(reader.result);
            };
            reader.readAsDataURL(droppedFile);
        }
    };

    // Handle Form Submit
    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (!title.trim()) {
            setErrorMessage('Le titre est requis.');
            return;
        }

        if (!file && !content.trim() && !editingMedia) {
            setErrorMessage('Vous devez uploader un fichier ou spécifier une URL/contenu.');
            return;
        }

        setBusy(true);
        try {
            const formData = new FormData();
            if (editingMedia) {
                formData.append('id', editingMedia.id);
            }
            formData.append('title', title);
            formData.append('type', type);
            formData.append('description', description);
            formData.append('keywords', keywords);
            formData.append('content', content);
            
            if (file) {
                formData.append('media_file', file);
            }

            const action = editingMedia ? 'update' : 'create';
            const res = await apiClient(`${BASE}/admin_media.php?action=${action}`, {
                method: 'POST',
                body: formData
            });

            const json = await res.json();
            if (json.success) {
                setSuccessMessage(json.message || 'Opération réussie !');
                setTimeout(() => {
                    setIsFormOpen(false);
                    loadMedia();
                }, 1000);
            } else {
                setErrorMessage(json.message || 'Une erreur est survenue.');
            }
        } catch (err) {
            setErrorMessage('Erreur réseau ou serveur.');
            console.error(err);
        } finally {
            setBusy(false);
        }
    };

    // Handle Deletion
    const handleDelete = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce média définitivement ? Cette action nettoiera également les fichiers stockés localement et annulera les associations.')) {
            return;
        }

        try {
            const res = await apiClient(`${BASE}/admin_media.php?action=delete`, {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            const json = await res.json();
            if (json.success) {
                loadMedia();
            } else {
                alert(json.message || 'Erreur lors de la suppression.');
            }
        } catch (err) {
            console.error("Error deleting media:", err);
            alert('Erreur réseau ou serveur.');
        }
    };

    // Copy URL helper
    const handleCopyUrl = (url, id) => {
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Filters
    const filteredMedia = mediaList.filter(media => {
        const matchesSearch = 
            (media.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (media.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (media.keywords || '').toLowerCase().includes(searchQuery.toLowerCase());
        
        const matchesType = typeFilter === 'all' || media.type === typeFilter;
        
        return matchesSearch && matchesType;
    });

    const formatSize = (bytes) => {
        if (!bytes) return '—';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    };

    return (
        <div className="space-y-6">
            {/* Header section with actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Bibliothèque Médias ({filteredMedia.length})</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Gérez les schémas, illustrations et ressources multimédias d'Axiomus</p>
                </div>
                <button onClick={handleOpenCreate}
                    className="self-start sm:self-center px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-semibold shadow-lg shadow-cyan-500/10 hover:shadow-cyan-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer">
                    + Nouveau Média
                </button>
            </div>

            {/* Filter and Search Bar */}
            <div className="flex flex-col md:flex-row gap-3 bg-white dark:bg-gray-900 p-4 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm">
                <div className="flex-1 relative">
                    <input type="text" placeholder="Rechercher par titre, description, mots-clés..." 
                        value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all" />
                    <span className="absolute left-3.5 top-2.5 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.602 10.602Z" />
                        </svg>
                    </span>
                </div>
                <div className="flex gap-2 min-w-[200px]">
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                        <option value="all">Tous les types</option>
                        <option value="image">Images</option>
                        <option value="video">Vidéos</option>
                        <option value="audio">Audios</option>
                        <option value="other">Autres</option>
                    </select>
                </div>
            </div>

            {/* Media Grid */}
            {loading ? (
                <div className="flex justify-center items-center py-32">
                    <div className="relative w-12 h-12">
                        <div className="absolute inset-0 rounded-full border-4 border-cyan-500/20"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-t-cyan-500 animate-spin"></div>
                    </div>
                </div>
            ) : filteredMedia.length === 0 ? (
                <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10">
                    <p className="text-gray-400 text-sm">Aucun média trouvé dans la bibliothèque.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredMedia.map(media => {
                        const isImage = media.type === 'image';
                        const isVideo = media.type === 'video';
                        const isAudio = media.type === 'audio';

                        return (
                            <div key={media.id} 
                                className="group relative flex flex-col bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden hover:shadow-xl hover:border-cyan-500/30 transition-all duration-300">
                                
                                {/* Media Preview Container */}
                                <div onClick={() => setPreviewMedia(media)}
                                    className="relative aspect-video bg-gray-50 dark:bg-gray-950 flex items-center justify-center overflow-hidden border-b border-gray-100 dark:border-white/5 cursor-zoom-in hover:opacity-95 transition-opacity"
                                >
                                    {isImage && (
                                        <img src={media.content} alt={media.title} 
                                            className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500" 
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://placehold.co/600x400/png?text=Erreur+Chargement';
                                            }} />
                                    )}
                                    {isVideo && (
                                        <div className="w-full h-full relative group/video">
                                            <video src={media.content} className="w-full h-full object-cover" preload="metadata" />
                                            <div className="absolute inset-0 bg-slate-900/40 flex items-center justify-center">
                                                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md text-white group-hover/video:scale-110 transition-transform">
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 ml-0.5">
                                                        <path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clipRule="evenodd" />
                                                    </svg>
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    {isAudio && (
                                        <div className="flex flex-col items-center justify-center gap-2 p-4 text-cyan-600 dark:text-cyan-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12">
                                                <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.063.922-2.063 2.063v4.875c0 1.141.922 2.062 2.063 2.062h1.932l4.5 4.5c.944.945 2.56.276 2.56-1.06V4.06ZM17.78 9.22a.75.75 0 1 0-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 0 0 1.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 1 0 1.06-1.06L20.56 12l1.72-1.72a.75.75 0 0 0-1.06-1.06l-1.72 1.72-1.72-1.72Z" />
                                            </svg>
                                            <span className="text-xs font-semibold uppercase tracking-wider">Fichier Audio</span>
                                        </div>
                                    )}
                                    {!isImage && !isVideo && !isAudio && (
                                        <div className="flex flex-col items-center justify-center gap-2 p-4 text-purple-600 dark:text-purple-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-12 h-12">
                                                <path fillRule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.154-.938 2.091-2.091 2.091H5.625A2.25 2.25 0 0 1 3.375 20.25V3.75A2.25 2.25 0 0 1 5.625 1.5ZM16.5 9h.375a1.875 1.875 0 0 0 1.875-1.875V6.75A1.875 1.875 0 0 0 16.5 4.875h-.375v2.25c0 1.036.84 1.875 1.875 1.875Z" clipRule="evenodd" />
                                            </svg>
                                            <span className="text-xs font-semibold uppercase tracking-wider">Ressource</span>
                                        </div>
                                    )}

                                    {/* Action hovering overlays */}
                                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => handleOpenEdit(media)} title="Modifier"
                                            className="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-200 hover:text-cyan-500 shadow-md transition-all cursor-pointer">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                            </svg>
                                        </button>
                                        <button onClick={() => handleDelete(media.id)} title="Supprimer"
                                            className="p-2 rounded-lg bg-white/90 dark:bg-gray-800/90 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shadow-md transition-all cursor-pointer">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                            </svg>
                                        </button>
                                    </div>
                                    <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-sm text-[10px] font-semibold text-white uppercase tracking-wider">
                                        {media.type}
                                    </span>
                                    {media.is_local && (
                                        <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-cyan-600/80 backdrop-blur-sm text-[10px] font-semibold text-white uppercase tracking-wider" title="Stocké localement sur le serveur">
                                            Local
                                        </span>
                                    )}
                                </div>

                                {/* Media Details */}
                                <div className="p-4 flex-1 flex flex-col justify-between gap-3 text-left">
                                    <div className="space-y-1">
                                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 line-clamp-1 flex items-center [&_p]:inline [&_p]:m-0">{renderContent(media.title)}</h3>
                                        {media.description ? (
                                            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 [&_p]:inline [&_p]:m-0">{renderContent(media.description)}</div>
                                        ) : (
                                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">Aucune description.</p>
                                        )}
                                    </div>

                                    {/* Keywords / Tags */}
                                    {media.keywords && (
                                        <div className="flex flex-wrap gap-1 items-center">
                                            {(expandedMediaTags[media.id] 
                                                ? media.keywords.split(',') 
                                                : media.keywords.split(',').slice(0, 3)
                                            ).map((kw, i) => (
                                                <button key={i}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSearchQuery(kw.trim());
                                                    }}
                                                    className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-white/5 text-gray-500 hover:text-cyan-600 dark:text-gray-400 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/20 cursor-pointer transition-all active:scale-95"
                                                    title={`Filtrer par #${kw.trim()}`}
                                                >
                                                    #{kw.trim()}
                                                </button>
                                            ))}
                                            {media.keywords.split(',').length > 3 && (
                                                <button 
                                                    onClick={() => toggleExpandTags(media.id)}
                                                    className="text-[9px] px-1.5 py-0.5 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/30 rounded font-bold cursor-pointer transition-all active:scale-95"
                                                    title={expandedMediaTags[media.id] ? "Réduire les mots-clés" : "Afficher tous les mots-clés"}
                                                >
                                                    {expandedMediaTags[media.id] ? '▲ Réduire' : `+${media.keywords.split(',').length - 3}`}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Copy URL & Info Footer */}
                                    <div className="pt-2 border-t border-gray-100 dark:border-white/5 flex items-center justify-between text-[11px] text-gray-400">
                                        <span>{media.file_size ? formatSize(media.file_size) : 'Lien Web'}</span>
                                        <button onClick={() => handleCopyUrl(media.content, media.id)}
                                            className={`px-2 py-1 rounded-lg font-medium flex items-center gap-1 transition-all cursor-pointer ${
                                                copiedId === media.id 
                                                    ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400' 
                                                    : 'bg-gray-50 dark:bg-white/5 hover:bg-cyan-50 dark:hover:bg-cyan-950/20 hover:text-cyan-600 dark:hover:text-cyan-400'
                                            }`}>
                                            {copiedId === media.id ? (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                                                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                                                    </svg>
                                                    Copié
                                                </>
                                            ) : (
                                                <>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-3 h-3">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0A2.247 2.247 0 0 1 13.5 4.75h-3c-.903 0-1.687-.532-2.046-1.294m9.332 0c.236.425.378.913.378 1.433v13.5c0 1.657-1.343 3-3 3H5.25a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3h1.378c.425 0 .866.142 1.25.378m9.332 0c-1.68 0-3.037-1.357-3.037-3.037m-7.332 3.037A2.247 2.247 0 0 0 5.25 4.75h1.378M9 21h6a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1Zm-5-4h2v2H4v-2Zm1.5-6h7.5A1.5 1.5 0 0 0 14.5 9.5v-1A1.5 1.5 0 0 0 13 7H5.5A1.5 1.5 0 0 0 4 8.5v1A1.5 1.5 0 0 0 5.5 11Z" />
                                                    </svg>
                                                    Copier l'URL
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Modal for Creating / Editing Media */}
            <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)}
                title={editingMedia ? 'Modifier le Média' : 'Ajouter un Nouveau Média'}
                subtitle={editingMedia ? `ID: #${editingMedia.id}` : 'Remplissez les détails et téléversez ou spécifiez l\'URL de votre ressource'}
                size="lg">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {errorMessage && (
                        <div className="p-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-xl">
                            {errorMessage}
                        </div>
                    )}
                    {successMessage && (
                        <div className="p-3 text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30 rounded-xl">
                            {successMessage}
                        </div>
                    )}

                    {/* IA Assistance Panel */}
                    <div className="p-4 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 flex flex-col sm:flex-row items-center justify-between gap-3 text-left">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl animate-pulse">✨</span>
                            <div>
                                <h4 className="text-xs font-bold text-gray-800 dark:text-white uppercase tracking-wider">Assistant IA Axiomus</h4>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Laissez l'IA analyser le média pour vous suggérer un titre, une description pédagogique et des tags.</p>
                            </div>
                        </div>
                        <button type="button" onClick={handleAiSuggest} disabled={aiLoading || (!file && !content)}
                            className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500 disabled:opacity-40 text-white text-xs font-semibold shadow-md transition-all flex items-center gap-1.5 cursor-pointer shrink-0">
                            {aiLoading ? (
                                <>
                                    <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Analyse en cours...
                                </>
                            ) : (
                                <>
                                    <span>🪄</span>
                                    <span>Générer par l'IA</span>
                                </>
                            )}
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-left">
                        {/* Title and details inputs */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Titre du média</label>
                                <input type="text" value={title} onChange={e => setTitle(e.target.value)} required placeholder="Ex: Schéma du théorème de Pythagore"
                                    className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Type de média</label>
                                    <select value={type} onChange={e => setType(e.target.value)}
                                        className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500">
                                        <option value="image">Image</option>
                                        <option value="video">Vidéo</option>
                                        <option value="audio">Audio</option>
                                        <option value="other">Autre</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Mots-clés (tags)</label>
                                    <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="Ex: math, triangle, pythagore"
                                        className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Description</label>
                                <textarea value={description} onChange={e => setDescription(e.target.value)} rows="3" placeholder="Description du schéma pour l'accessibilité ou l'IA..."
                                    className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none" />
                            </div>
                        </div>

                        {/* File Upload / Source Selector */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Source / Contenu du média</label>
                                
                                {/* Drag & Drop Area */}
                                <div onClick={() => fileInputRef.current?.click()}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    className={`relative h-44 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center gap-2 p-4 cursor-pointer transition-all duration-300 group ${
                                        isDragging 
                                            ? 'border-cyan-500 bg-cyan-500/10 scale-[1.02] shadow-lg shadow-cyan-500/5' 
                                            : 'border-gray-300 dark:border-white/10 hover:border-cyan-500 dark:hover:border-cyan-400 bg-gray-50/50 dark:bg-white/[0.02] hover:bg-cyan-500/5'
                                    }`}
                                >
                                    <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                                    
                                    {filePreview ? (
                                        <div className="w-full h-full relative flex items-center justify-center overflow-hidden rounded-xl">
                                            {type === 'image' && <img src={filePreview} alt="Aperçu" className="max-h-full max-w-full object-contain" />}
                                            {type === 'video' && <video src={filePreview} className="max-h-full max-w-full" muted autoPlay loop />}
                                            {type !== 'image' && type !== 'video' && (
                                                <div className="text-center text-cyan-500">
                                                    <span className="text-xs font-bold block mb-1">Fichier sélectionné :</span>
                                                    <span className="text-xs truncate max-w-xs">{file?.name}</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : editingMedia && editingMedia.content && !file ? (
                                        <div className="w-full h-full relative flex items-center justify-center overflow-hidden rounded-xl">
                                            {type === 'image' && <img src={editingMedia.content} alt="Actuel" className="max-h-full max-w-full object-contain" />}
                                            {type === 'video' && <video src={editingMedia.content} className="max-h-full max-w-full" muted controls />}
                                            {type !== 'image' && type !== 'video' && (
                                                <div className="text-center text-gray-400">
                                                    <span className="text-xs block mb-1 font-bold">Média actuel :</span>
                                                    <span className="text-xs truncate max-w-xs block">{editingMedia.content}</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <span className="p-3 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-white/5 shadow-sm group-hover:scale-110 transition-transform">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6 text-gray-400 group-hover:text-cyan-500">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                                                </svg>
                                            </span>
                                            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Choisir un fichier...</span>
                                            <span className="text-[10px] text-gray-400">Glissez-déposez ou cliquez pour parcourir</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Manual content input */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Ou URL / Contenu alternatif</label>
                                <input type="text" value={content} onChange={e => setContent(e.target.value)} placeholder="https://example.com/image.jpg"
                                    className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
                                <span className="text-[10px] text-gray-400 mt-1 block">Si aucun fichier n'est téléversé, cette valeur sera utilisée.</span>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="pt-4 border-t border-gray-100 dark:border-white/5 flex justify-end gap-3">
                        <button type="button" onClick={() => setIsFormOpen(false)}
                            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-all cursor-pointer">
                            Annuler
                        </button>
                        <button type="submit" disabled={busy}
                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-sm font-semibold shadow-lg shadow-cyan-500/10 disabled:opacity-50 transition-all cursor-pointer">
                            {busy ? 'Traitement...' : editingMedia ? 'Sauvegarder' : 'Créer'}
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Immersive Media Lightbox Preview Modal */}
            <Modal
                isOpen={!!previewMedia}
                onClose={() => setPreviewMedia(null)}
                title={previewMedia ? <div className="flex items-center [&_p]:inline [&_p]:m-0">{renderContent(previewMedia.title)}</div> : "Aperçu du Média"}
                subtitle={previewMedia ? `Type: ${previewMedia.type.toUpperCase()}` : ""}
                size="xl"
            >
                {previewMedia && (
                    <div className="flex flex-col items-center justify-center gap-6">
                        <div className="w-full flex items-center justify-center overflow-hidden rounded-2xl bg-gray-50 dark:bg-black/25 p-4 border border-gray-100 dark:border-white/5">
                            {previewMedia.type === 'image' && (
                                <img src={previewMedia.content} alt={previewMedia.title} className="max-w-full max-h-[60vh] object-contain rounded-xl shadow-md" />
                            )}
                            {previewMedia.type === 'video' && (
                                <video src={previewMedia.content} className="max-w-full max-h-[60vh] rounded-xl shadow-md" controls autoPlay />
                            )}
                            {previewMedia.type === 'audio' && (
                                <div className="flex flex-col items-center gap-4 py-8 text-cyan-600 dark:text-cyan-400 text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-20 h-20 animate-pulse">
                                        <path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.063.922-2.063 2.063v4.875c0 1.141.922 2.062 2.063 2.062h1.932l4.5 4.5c.944.945 2.56.276 2.56-1.06V4.06ZM18.57 17.47a.75.75 0 1 1-1.06-1.06 7.5 7.5 0 0 0 0-10.61.75.75 0 0 1 1.06-1.06 9 9 0 0 1 0 12.73ZM15.9 14.8a.75.75 0 1 1-1.06-1.06 3.75 3.75 0 0 0 0-5.3a.75.75 0 0 1 1.06-1.06 5.25 5.25 0 0 1 0 7.42Z" />
                                    </svg>
                                    <audio src={previewMedia.content} controls className="w-80" autoPlay />
                                </div>
                            )}
                            {previewMedia.type !== 'image' && previewMedia.type !== 'video' && previewMedia.type !== 'audio' && (
                                <div className="flex flex-col items-center gap-4 py-8 text-purple-600 dark:text-purple-400 text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-20 h-20">
                                        <path fillRule="evenodd" d="M5.625 1.5H9a3.75 3.75 0 0 1 3.75 3.75v1.875c0 1.036.84 1.875 1.875 1.875H16.5a3.75 3.75 0 0 1 3.75 3.75v7.875c0 1.154-.938 2.091-2.091 2.091H5.625A2.25 2.25 0 0 1 3.375 20.25V3.75A2.25 2.25 0 0 1 5.625 1.5ZM16.5 9h.375a1.875 1.875 0 0 0 1.875-1.875V6.75A1.875 1.875 0 0 0 16.5 4.875h-.375v2.25c0 1.036.84 1.875 1.875 1.875Z" clipRule="evenodd" />
                                    </svg>
                                    <a href={previewMedia.content} target="_blank" rel="noreferrer" className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold shadow-lg text-sm transition-all">
                                        Télécharger / Ouvrir la ressource
                                    </a>
                                </div>
                            )}
                        </div>
                        
                        <div className="w-full space-y-4">
                            {previewMedia.description && (
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Description</h4>
                                    <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed [&_p]:inline [&_p]:m-0">{renderContent(previewMedia.description)}</div>
                                </div>
                            )}
                            {previewMedia.keywords && (
                                <div>
                                    <h4 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Mots-clés</h4>
                                    <div className="flex flex-wrap gap-1">
                                        {previewMedia.keywords.split(',').map((kw, i) => (
                                            <button key={i}
                                                onClick={() => {
                                                    setSearchQuery(kw.trim());
                                                    setPreviewMedia(null);
                                                }}
                                                className="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-white/5 text-gray-500 hover:text-cyan-600 dark:text-gray-400 dark:hover:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-950/20 cursor-pointer transition-all active:scale-95"
                                                title={`Filtrer par #${kw.trim()}`}
                                            >
                                                #{kw.trim()}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
